import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import { Segment } from 'entities/Segment/model/Segment'
import type { IParamsFiltersList, IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IParamsSendFrom } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import type { IEnrollmentFiltersParams } from 'widgets/ContactEnrollment/types/types'
import { SendFromStore } from 'widgets/SendFrom'

export type IPowerDialerEditorType = 'fromContacts' | 'fromFilters'
export type IPowerDialerMode = 'create' | 'edit' | 'duplicate'
export type IPowerDialerEditorOrigin = 'contacts' | 'conversations' | 'pdCampaigns'

export class PowerDialerStateStore {
  private _type: IPowerDialerEditorType = 'fromContacts'
  private _mode: IPowerDialerMode = 'create'
  private _origin: IPowerDialerEditorOrigin | null = null

  private _id: number | null = null
  private _name = ''
  private _nameError = ''

  private _step = 1
  private _modalId = ''
  private _showAlert = true
  private _disposeChangeTitleModal: IReactionDisposer | null = null

  private _loading = false
  private _draftLoading = false
  private _callLoading = false

  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _enrollmentStore: EnrollmentStore
  private _sendFromStore

  constructor(private _entities: IEntitiesStore) {
    this._sendFromStore = new SendFromStore(this._entities, {
      getDropdownItemsOptions: {
        excludeSIPTrunk: true,
        excludeAircall: true,
        excludeShortCode: true,
        showOnlyVerifiedToolFree: true,
      },
      disabledPFT: true,
      personalInboxesOnly: true,
      emptyStateError: 'Please select an inbox to call from',
    })
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: async () => {
            const { data } = await ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'core',
              skipValues: ['tag'],
            })
            return data
          },
        },
      },
      this._entities
    )
    makeAutoObservable(this)

    this._watchTitle()
  }

  private _watchTitle = () => {
    this._disposeChangeTitleModal?.()
    this._disposeChangeTitleModal = reaction(
      () => this.titleModal,
      (value) => {
        this._enrollmentStore.setTitleModal(value)
        this._updateModalTitle(value)
      }
    )
  }

  private _updateModalTitle = ({
    title,
    titleDividerContent,
  }: {
    title: string
    titleDividerContent: string
  }) => {
    const modal = modalStore.getModal(this._modalId)
    if (modal) {
      if ('title' in modal) modal.title = title
      if ('titleDividerContent' in modal) modal.titleDividerContent = titleDividerContent
    }
  }

  closeEditorModal = () => {
    modalStore.removeModal(this._modalId)
  }

  setDefaultInbox = () => {
    if (this._entities.inboxesStore.currentInbox instanceof Inbox) {
      this.sendFromStore.setSelectItem({
        inbox_id: this._entities.inboxesStore.currentInbox?.id,
      })
    }
  }

  getEnrollmentFiltersParams = async (
    filter: IParamsFiltersList
  ): Promise<IEnrollmentFiltersParams> => {
    const tagsIds = filter.tags ?? []
    const contactsIds = filter.contacts ?? []
    const segmentsIds = filter.segments ?? []

    const [tags, contacts = []] = await Promise.all([
      this._entities.tagsStore.fetchTags(tagsIds),
      this._entities.contactsStore.getByIds(contactsIds),
    ])
    const segments = segmentsIds.reduce<Segment[]>((acc, id) => {
      const item = this._entities.segmentsStore.getItem(id)
      if (item) acc.push(item)
      return acc
    }, [])
    const filters =
      filter.advanced?.map(
        (item) =>
          new Segment({
            id: -1,
            organization_id: 0,
            name: '',
            filters: item,
            is_favorite: false,
          })
      ) ?? []

    return { tags, segments, contacts, filters }
  }

  setType = (type: IPowerDialerEditorType) => {
    this._type = type
  }

  setMode = (mode: IPowerDialerMode) => {
    this._mode = mode
  }

  setOrigin = (origin: IPowerDialerEditorOrigin | null) => {
    this._origin = origin
  }

  setStep = (step: number) => {
    this._step = step
  }

  setId = (id: number | null) => {
    this._id = id
  }

  setModalId = (modalId: string) => {
    this._modalId = modalId
  }

  setName = (name: string) => {
    this._name = name
  }

  setNameError = (err: string) => {
    this._nameError = err
  }

  setLoading = (loading: boolean) => {
    this._loading = loading
  }

  setDraftLoading = (draftLoading: boolean) => {
    this._draftLoading = draftLoading
  }

  setCallLoading = (callLoading: boolean) => {
    this._callLoading = callLoading
  }

  setEnrollmentFilters = (filters: typeof this._enrollmentFilters) => {
    this._enrollmentFilters = filters
  }

  onChangeShowAlert = (showAlert: boolean) => {
    this._showAlert = showAlert
  }

  get sendFromStore() {
    return this._sendFromStore
  }

  get titleModal() {
    if (this._type === 'fromContacts') return { title: 'Power Dialer', titleDividerContent: '' }

    if (this._mode === 'edit')
      return {
        title: 'Edit Power Dialer',
        titleDividerContent: `${this._step} of 2`,
      }

    return { title: 'Power Dialer', titleDividerContent: `${this._step} of 2` }
  }

  get isNumberNotVerifiedCallerId() {
    const inbox = this._entities.inboxesStore.getItem(this.teamId)
    return inbox?.type === 'inbox' && inbox?.isNumberNotVerifiedCallerId
  }

  get editorActionTooltip() {
    const inbox = this._entities.inboxesStore.getItem(this.teamId)
    return inbox?.type === 'inbox' ? inbox?.callerIdFailedReason : null
  }

  get paramsFilters(): IParamsFiltersListGroups {
    return {
      filtersListGroups: {
        contacts: this._enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
        segments: this._enrollmentFilters.segments.map((segment) => segment.id) ?? [],
        tags: this._enrollmentFilters.tags.map((tag) => tag.id) ?? [],
        advanced: this._enrollmentFilters.filters.map((filter) => filter.filtersParams) ?? [],
      },
    }
  }

  get showAlert() {
    if (this._type === 'fromFilters') return false
    return this._showAlert
  }

  get step() {
    return this._step
  }

  get isEditMode() {
    return this._mode === 'edit'
  }

  get teamId() {
    return this.sendFromStore.inbox_id
  }

  get callFrom(): IParamsSendFrom | null {
    return this.sendFromStore.paramsSendFrom
  }

  get type() {
    return this._type
  }

  get mode() {
    return this._mode
  }

  get origin() {
    return this._origin
  }

  get id() {
    return this._id
  }

  get name() {
    return this._name
  }

  get nameError() {
    return this._nameError
  }

  get loading() {
    return this._loading
  }

  get draftLoading() {
    return this._draftLoading
  }

  get callLoading() {
    return this._callLoading
  }

  get enrollmentFilters() {
    return this._enrollmentFilters
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  reset = () => {
    this._type = 'fromContacts'
    this._mode = 'create'
    this._origin = null
    this._id = null
    this._name = ''
    this._nameError = ''
    this._loading = this._draftLoading = this._callLoading = false

    this._step = 1
    this._showAlert = true
    this._disposeChangeTitleModal?.()
    this._modalId = ''
    this.sendFromStore.reset()
    this._enrollmentFilters = EnrollmentFilters.Empty()
  }
}
